import React from 'react';
import { Auth } from '@aws-amplify/auth';
import { Form, Formik, FormikHelpers } from 'formik';
import { toast } from 'sonner';
import { FormFields } from '@/components/Inputs/FormFields';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FieldError } from '@/components/ui/field';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useConfirmEmailChangeMutation } from '@/generated/codegen';
import { useIsEffectivelyOnline } from '@/hooks/useIsEffectivelyOnline';
import { useAuthActions, useAuthState } from '@/utils/auth';
import {
  CodeValues,
  codeFormFields,
  codeFormSchema,
  cognitoCodeErrorMessage,
  cognitoHasConfirmedEmail,
  isGoogleSession,
  sendEmailCode,
} from '@/utils/emailCode';

const GENERIC_ERROR_MESSAGE = 'Ocurrió un error. Intentá de nuevo.';

/**
 * start: todavía no pidió el código. code: lo pidió y lo tiene que cargar.
 * confirm: ya lo cargó en Cognito y falta que la API cambie el email.
 * google: entró con Google, y el código es de su identidad con clave.
 */
type Step = 'start' | 'code' | 'confirm' | 'google';

const cognitoErrorMessage = (error: unknown): string =>
  cognitoCodeErrorMessage(error) ?? GENERIC_ERROR_MESSAGE;

const apiErrorMessage = (error: unknown): string =>
  error instanceof Error && error.message
    ? error.message
    : GENERIC_ERROR_MESSAGE;

/**
 * El email nuevo que pidió soporte: la persona pide el código, lo carga y su
 * email cambia. Hasta entonces sigue entrando con el anterior. «Ahora no» lo
 * cierra hasta la próxima vez que entra.
 */
export const EmailChangeDialog: React.FC = () => {
  const { user } = useAuthState();
  const online = useIsEffectivelyOnline('3g');
  const pendingEmail = user?.pending_email ?? null;
  const [dismissed, setDismissed] = React.useState<string | null>(null);

  if (!user || !pendingEmail || !online || dismissed === pendingEmail)
    return null;

  return (
    <EmailChangeSteps
      key={pendingEmail}
      currentEmail={user.email}
      pendingEmail={pendingEmail}
      onDismiss={() => setDismissed(pendingEmail)}
    />
  );
};

const EmailChangeSteps: React.FC<{
  currentEmail: string;
  pendingEmail: string;
  onDismiss: () => void;
}> = ({ currentEmail, pendingEmail, onDismiss }) => {
  const { refreshUser } = useAuthActions();
  const [confirmEmailChange] = useConfirmEmailChangeMutation();
  const [step, setStep] = React.useState<Step | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  // Retoma: si ya cargó el código y la API no llegó a cambiar el email, sólo
  // falta confirmarlo.
  React.useEffect(() => {
    let cancelled = false;
    Promise.all([isGoogleSession(), cognitoHasConfirmedEmail(pendingEmail)])
      .then(([google, confirmed]) => {
        if (cancelled) return;
        setStep(google ? 'google' : confirmed ? 'confirm' : 'start');
      })
      .catch(() => {
        if (!cancelled) setStep('start');
      });
    return () => {
      cancelled = true;
    };
  }, [pendingEmail]);

  const finish = async () => {
    try {
      await confirmEmailChange();
    } catch (e) {
      setStep('confirm');
      setError(apiErrorMessage(e));
      return;
    }
    toast.success(`Listo: tu email ahora es ${pendingEmail}.`);
    await refreshUser();
  };

  const requestCode = async () => {
    setError(null);
    setBusy(true);
    try {
      await sendEmailCode(pendingEmail);
      setStep('code');
    } catch (e) {
      setError(cognitoErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (
    values: CodeValues,
    { setSubmitting }: FormikHelpers<CodeValues>
  ) => {
    setError(null);
    try {
      await Auth.verifyCurrentUserAttributeSubmit('email', values.code);
    } catch (e) {
      setError(cognitoErrorMessage(e));
      setSubmitting(false);
      return;
    }
    await finish();
    setSubmitting(false);
  };

  const confirm = async () => {
    setError(null);
    setBusy(true);
    await finish();
    setBusy(false);
  };

  // Hasta saber en qué paso está, no aparece.
  if (step === null) return null;

  const notNow = (
    <Button type="button" variant="ghost" onClick={onDismiss}>
      Ahora no
    </Button>
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onDismiss()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirmá tu email nuevo</DialogTitle>
          <DialogDescription>
            {step === 'code'
              ? `Te mandamos un código a ${pendingEmail}.`
              : step === 'confirm'
                ? `Ya cargaste el código de ${pendingEmail}. Falta terminar el cambio.`
                : step === 'google'
                  ? `Soporte pidió cambiar tu email a ${pendingEmail}. Para confirmarlo, entrá con ${currentEmail} y tu clave.`
                  : `Soporte pidió cambiar tu email a ${pendingEmail}. Hasta que lo confirmes, seguís entrando con ${currentEmail}.`}
          </DialogDescription>
        </DialogHeader>

        {step === 'start' && (
          <div className="flex flex-col gap-4">
            {error && <FieldError>{error}</FieldError>}
            <Button type="button" disabled={busy} onClick={requestCode}>
              {busy ? <LoadingSpinner /> : 'Mandame el código'}
            </Button>
            {notNow}
          </div>
        )}

        {step === 'code' && (
          <Formik<CodeValues>
            initialValues={{ code: '' }}
            validationSchema={codeFormSchema}
            onSubmit={submitCode}
          >
            {({ isSubmitting }) => (
              <Form className="flex flex-col gap-4">
                <FormFields fields={codeFormFields} />
                {error && <FieldError>{error}</FieldError>}
                <Button type="submit" disabled={isSubmitting || busy}>
                  {isSubmitting ? <LoadingSpinner /> : 'Confirmar'}
                </Button>
                <Button
                  type="button"
                  variant="link"
                  disabled={isSubmitting || busy}
                  onClick={requestCode}
                >
                  Mandame otro código
                </Button>
                {notNow}
              </Form>
            )}
          </Formik>
        )}

        {step === 'confirm' && (
          <div className="flex flex-col gap-4">
            {error && <FieldError>{error}</FieldError>}
            <Button type="button" disabled={busy} onClick={confirm}>
              {busy ? <LoadingSpinner /> : 'Terminar'}
            </Button>
            {notNow}
          </div>
        )}

        {step === 'google' && notNow}
      </DialogContent>
    </Dialog>
  );
};
